import { inject, Injectable, InjectionToken } from "@angular/core";
import { lastValueFrom } from "rxjs";

import {
	DataBundle,
	FileDownloadBundle,
	PagedCollectionBundle,
	PagedCollectionBundleWithActions,
	PagedCollectionData,
} from "@intocare/datatypes/lib/bundle";
import { omitNullable } from "@intocare/util";

import { CommunicationGroupsSearchParams, CommunicationGroupSummary } from "./communication-groups";
import {
	CommunicationMessage,
	CommunicationState,
	MessagesSearchParams,
	MessageSummary,
	RecipientsSearchParams,
	SentMessageRecipient,
	SentMessageSummary,
} from "./communication-messages";
import { AppHttpClient } from "../../shared/base/app-http-client";

export const COMMUNICATION_DATA_API_PREFIX = new InjectionToken<string>("COMMUNICATION_DATA_API_PREFIX");

@Injectable({ providedIn: "root" })
export class SharedCommunicationDataApi {
	private readonly prefix = inject(COMMUNICATION_DATA_API_PREFIX);
	private readonly http = inject(AppHttpClient);

	getMessagesState() {
		return lastValueFrom(this.http.get<DataBundle<CommunicationState>>(`${this.prefix}/messages-state`));
	}

	/**
	 * Get (received) messages for the principal.
	 *
	 * @param params - Query parameters for this request.
	 * @param [params.siteId] - Context in which messages are shown, to indicate which are destined for the current site.
	 * @param params.search - Search parameters, like q and paging.
	 */
	getMessages(params: { siteId?: string; search: MessagesSearchParams }) {
		return lastValueFrom(
			this.http.get<DataBundle<PagedCollectionData<DataBundle<MessageSummary>> & CommunicationState>>(
				`${this.prefix}/messages`,
				{ params: omitNullable({ site_id: params.siteId, ...params.search }) },
			),
		);
	}

	readMessageById(params: { messageId: string }) {
		return lastValueFrom(
			this.http.post<DataBundle<CommunicationMessage>>(`${this.prefix}/read-message-by-id`, {
				messageId: params.messageId,
			}),
		);
	}

	getMessageAttachmentUrl(params: { messageId: string; uploadId: string }) {
		return lastValueFrom(
			this.http.get<FileDownloadBundle>(`${this.prefix}/url-for-message-attachment-upload`, {
				params: { message_id: params.messageId, upload_id: params.uploadId },
			}),
		);
	}

	getSentMessages(params: { search: MessagesSearchParams }) {
		return lastValueFrom(
			this.http.get<PagedCollectionBundle<DataBundle<SentMessageSummary>>>(`${this.prefix}/sent-messages`, {
				params: omitNullable(params.search),
			}),
		);
	}

	getSentMessageById(params: { messageId: string }) {
		return lastValueFrom(
			this.http.get<DataBundle<CommunicationMessage>>(`${this.prefix}/sent-message-by-id`, {
				params: { message_id: params.messageId },
			}),
		);
	}

	getSentMessageRecipientsById(params: { messageId: string; search: RecipientsSearchParams }) {
		return lastValueFrom(
			this.http.get<PagedCollectionBundle<DataBundle<SentMessageRecipient>>>(
				`${this.prefix}/sent-message-recipients-by-id`,
				{ params: omitNullable({ message_id: params.messageId, ...params.search }) },
			),
		);
	}

	getCommunicationGroups(params: { search: CommunicationGroupsSearchParams }) {
		return lastValueFrom(
			this.http.get<PagedCollectionBundleWithActions<DataBundle<CommunicationGroupSummary>, "self" | "create">>(
				`${this.prefix}/communication-groups`,
				{ params: omitNullable(params.search) },
			),
		);
	}
}
